import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  recognitionKeys,
  useRecognitionCreateQuery,
  useRecognitionUpdateQuery,
} from 'hooks/api';
import { useAppLocation } from 'hooks/helpers';
import { useModal } from 'hooks/ui';
import { recognitionRoutes } from 'router/recognitionRoutes';
import { useAISuggestionStore } from 'store/ai-suggestion';
import {
  recognitionTemplate,
  resetDraft,
  setRecognitions,
  useRecognitionStore,
} from 'store/recognition';
import { useLeaveAIAssistant } from './useLeaveAIAssistant';
import { AllStatuses } from 'interfaces';

type HookArgs = {
  onSuccess: () => void;
  onAbort: () => void;
};

export const useRecognitionSwitcher = () => {
  const queryClient = useQueryClient();
  const modal = useModal();
  const { draft: draftRecognition, errors } = useRecognitionStore();
  const { isCreate } = useAppLocation(recognitionRoutes);
  const activeAISuggestion = useAISuggestionStore(
    (state) => state.activeAISuggestion,
  );
  const { mutate: createRecognition } = useRecognitionCreateQuery();
  const { mutate: updateRecognition } = useRecognitionUpdateQuery();
  const isFormDraftable = !draftRecognition.title.trim().length;
  const leaveAIAssistant = useLeaveAIAssistant();

  const hasErrors = useMemo(
    () => Object.values(errors).some((field) => field.length > 0),
    [errors],
  );

  const promptToUpdate = ({ onSuccess, onAbort }: HookArgs): void =>
    modal.save({
      text: 'Do you want to save the changes? \n Otherwise the changes will be lost.',
      highlights: ['save'],
      onSuccess: (): void => {
        updateRecognition(draftRecognition);

        onSuccess();
      },
      onAbort: (): void => {
        resetDraft();
        setRecognitions(queryClient.getQueryData(recognitionKeys.all) ?? []);

        onAbort();
      },
      isDisabled: isFormDraftable || hasErrors,
    });

  const promptToCreate = ({ onSuccess, onAbort }: HookArgs): void =>
    modal.save({
      text: 'Do you want to save the changes? \n Otherwise the changes will be lost.',
      highlights: ['save'],
      onSuccess: (): void => {
        createRecognition(draftRecognition);

        onSuccess();
      },
      onAbort: (): void => {
        resetDraft();

        onAbort();
      },
      isDisabled: isFormDraftable || hasErrors,
    });

  return ({ onSuccess, onAbort }: HookArgs) => {
    const isDraftDirty = draftRecognition.status === AllStatuses.NOT_SAVED;
    const isDraftEmpty =
      JSON.stringify(draftRecognition) === JSON.stringify(recognitionTemplate);

    if (isDraftDirty && (!isDraftEmpty || isCreate)) {
      if (!draftRecognition.id) {
        return promptToCreate({ onSuccess, onAbort });
      }

      return promptToUpdate({ onSuccess, onAbort });
    }

    if (activeAISuggestion) {
      return leaveAIAssistant({
        onSuccess,
        onAbort: () => {},
      });
    }

    return onSuccess();
  };
};
